import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, extractError } from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  // null = checking, false = anonymous, object = authed
  const [user, setUser] = useState(null);

  const fetchMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return data;
    } catch (e) {
      setUser(false);
      return null;
    }
  }, []);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data.organiser);
      return { ok: true, data };
    } catch (e) {
      return { ok: false, error: extractError(e) };
    }
  };

  const signup = async (payload) => {
    try {
      const { data } = await api.post("/auth/signup", payload);
      return { ok: true, data };
    } catch (e) {
      return { ok: false, error: extractError(e) };
    }
  };

  const activate = async (token) => {
    try {
      const { data } = await api.post("/auth/activate", { token });
      setUser(data.organiser);
      return { ok: true, data };
    } catch (e) {
      return { ok: false, error: extractError(e) };
    }
  };

  const forgotPassword = async (email) => {
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      return { ok: true, data };
    } catch (e) {
      return { ok: false, error: extractError(e) };
    }
  };

  const resetPassword = async (token, password) => {
    try {
      const { data } = await api.post("/auth/reset-password", { token, password });
      return { ok: true, data };
    } catch (e) {
      return { ok: false, error: extractError(e) };
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (_) {
      /* ignore */
    }
    setUser(false);
  };

  const updateProfile = async (payload) => {
    try {
      const { data } = await api.patch("/organiser/profile", payload);
      setUser(data);
      return { ok: true, data };
    } catch (e) {
      return { ok: false, error: extractError(e) };
    }
  };

  const value = {
    user,
    isAuthed: !!user && user !== false,
    checking: user === null,
    login,
    signup,
    activate,
    logout,
    forgotPassword,
    resetPassword,
    updateProfile,
    refetchMe: fetchMe,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
